import { useCallback, useEffect, useState, useRef } from "react";
import axios from "axios";
import { search } from "~src/api";
import { FullBrand } from "~src/types/brand";
import { DefaultBrandDiscoverFilters } from "~src/api/discover";
import * as Sentry from "@sentry/react";

export interface DiscoverBrandContext {
  data: FullBrand[];
  total: number | null;
  loading: boolean;
  error: boolean;
  initialLoad: boolean;
  fetchData: () => Promise<void>;
  loadMore: () => Promise<void>;
  reset: () => Promise<void>;
}

export const useInfiniteDiscoverBrands = (
  params: search.BrandDiscoverParams,
): DiscoverBrandContext => {
  const { page } = params;
  const initialLoadDone = useRef(false);
  const isFetching = useRef(false);
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Keep track of which params have been loaded in this component instance
  const sessionLoadedParamKeys = useRef(new Set<string>());

  const [data, setData] = useState<FullBrand[]>([]);
  const [total, setTotal] = useState<null | number>(0);
  const [currentPage, setCurrentPage] = useState<number>(page || 1);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  
  // Create a unique key for the current params
  const paramKey = JSON.stringify(params);

  const fetchData = useCallback(async () => {
    if (isFetching.current) return;
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = null;
    }
    
    isFetching.current = true;
    setLoading(true);

    try {
      // Fetch fresh data from backend (Redis caching handles performance)
      const response = await search.getBrandDiscoverResult(params);
      setData(response.results);
      setHasMore(!!response.next);
      setTotal(response.count);
      setCurrentPage(2);

      // Mark as loaded for this session
      sessionLoadedParamKeys.current.add(paramKey);
      initialLoadDone.current = true;
    } catch (err) {
      // Handle 404 as empty results, not an error
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        setData([]);
        setHasMore(false);
        setTotal(0);
        sessionLoadedParamKeys.current.add(paramKey);
        initialLoadDone.current = true;
      } else {
        Sentry.withScope((scope) => {
          scope.setTag("module", "useInfiniteDiscoverBrands");
          scope.setExtra("operation", "fetchData");
          Sentry.captureException(err);
        });
        setError(true);
      }
    } finally {
      setLoading(false);
      setInitialLoad(false);
      isFetching.current = false;
    }
  }, [params, paramKey]);

  const loadMore = useCallback(async () => {
    if (isFetching.current || !hasMore) return;
    
    isFetching.current = true;
    setLoading(true);
    
    try {
      const nextPageParams = {
        ...params,
        page: currentPage,
      };
      
      const response = await search.getBrandDiscoverResult(nextPageParams);

      setData((prevData) => [...prevData, ...response.results]);
      setHasMore(!!response.next);
      setCurrentPage((prev) => prev + 1);
    } catch (err) {
      // Handle 404 as no more results, not an error
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        setHasMore(false);
      } else {
        Sentry.withScope((scope) => {
          scope.setTag("module", "useInfiniteDiscoverBrands");
          scope.setExtra("operation", "loadMore");
          Sentry.captureException(err);
        });
        setError(true);
      }
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  }, [params, currentPage, hasMore]);

  const reset = useCallback(async () => {
    if (isFetching.current) return;
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = null;
    }
    
    isFetching.current = true;
    
    setData([]);
    setTotal(null);
    setInitialLoad(true);
    setCurrentPage(1);
    setLoading(true);
    initialLoadDone.current = false;
    
    // Prepare the reset params
    const resetParams = {
      ...DefaultBrandDiscoverFilters,
      ordering: "-scaling_score_ranking",
      page: 1,
    };
    
    try {
      // Fetch fresh data from backend (Redis caching handles performance)
      const response = await search.getBrandDiscoverResult(resetParams);
      setData(response.results);
      setHasMore(!!response.next);
      setTotal(response.count);
      setCurrentPage(2);

      // Mark as loaded for this session
      const resetParamKey = JSON.stringify(resetParams);
      sessionLoadedParamKeys.current.add(resetParamKey);
    } catch (err) {
      // Handle 404 as empty results, not an error
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        setData([]);
        setHasMore(false);
        setTotal(0);
        const resetParamKey = JSON.stringify({
          ...DefaultBrandDiscoverFilters,
          ordering: "-scaling_score_ranking",
          page: 1,
        });
        sessionLoadedParamKeys.current.add(resetParamKey);
      } else {
        Sentry.withScope((scope) => {
          scope.setTag("module", "useInfiniteDiscoverBrands");
          scope.setExtra("operation", "reset");
          Sentry.captureException(err);
        });
        setError(true);
      }
    } finally {
      setLoading(false);
      setInitialLoad(false);
      isFetching.current = false;
    }
  }, []);

  // Track previous params to detect significant changes
  const prevParamsRef = useRef<string | null>(null);

  // Reset state when ordering or other significant parameters change
  useEffect(() => {
    const prevParams = prevParamsRef.current ? JSON.parse(prevParamsRef.current) : null;
    const currentParams = params;
    
    // Check if ordering has changed (this indicates user sorting action)
    if (prevParams && prevParams.ordering !== currentParams.ordering) {
      // Reset pagination and state for new sort order
      setData([]);
      setCurrentPage(1);
      setHasMore(false);
      setInitialLoad(true);
      initialLoadDone.current = false;
    }
    
    prevParamsRef.current = JSON.stringify(params);
  }, [params]);

  // Initial data fetch
  useEffect(() => {
    if (!initialLoadDone.current && !sessionLoadedParamKeys.current.has(paramKey)) {
      fetchData();
    }
  }, [fetchData, paramKey]);

  return {
    data,
    total,
    loading,
    error,
    initialLoad,
    fetchData,
    loadMore,
    reset,
  };
};
